<template>
  <div class="mod-home" v-loading="loading">
    <div class="home-toolbar">
      <el-radio-group v-model="homeViewMode" size="small" @change="onViewModeChange">
        <el-radio-button label="workbench">工作台</el-radio-button>
        <el-radio-button label="flow">业务流程图</el-radio-button>
      </el-radio-group>
      <div class="home-toolbar-actions">
        <el-button v-if="homeViewMode === 'workbench'" size="mini" icon="el-icon-setting" @click="configVisible = true">布局设置</el-button>
        <el-button size="mini" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
      </div>
    </div>

    <div v-show="homeViewMode === 'workbench'">
      <section v-for="widgetKey in workbenchWidgets" :key="widgetKey" class="home-section">
        <el-card v-if="widgetKey === 'welcome'" shadow="never" class="home-welcome-card">
          <h2 class="home-welcome-title">你好，{{ displayName }}</h2>
          <p class="home-welcome-sub">
            <span>{{ todayText }}</span>
            <span v-if="tenantName"> · {{ tenantName }}</span>
            <span v-if="financePeriodLabel" class="home-welcome-period"> · {{ financePeriodLabel }}</span>
          </p>
        </el-card>

        <el-row v-else-if="widgetKey === 'inventoryKpi' && inventoryKpi" :gutter="12">
          <el-col :xs="12" :sm="6"><el-card shadow="hover" class="home-kpi-card" @click.native="openMenu('inventory/inventoryQuantity', '库存查询')"><div class="home-kpi-value">{{ formatNum(inventoryKpi.quantityTotal) }}</div><div class="home-kpi-label">库存总量</div></el-card></el-col>
          <el-col :xs="12" :sm="6"><el-card shadow="hover" class="home-kpi-card" @click.native="openMenu('inventory/inventoryQuantity', '库存查询')"><div class="home-kpi-value">{{ formatNum(inventoryKpi.usableQuantityTotal) }}</div><div class="home-kpi-label">可用总量</div></el-card></el-col>
          <el-col :xs="12" :sm="6"><el-card shadow="hover" class="home-kpi-card" @click.native="openMenu('inventory/inventoryQuantity', '库存查询')"><div class="home-kpi-value">{{ inventoryKpi.materialCount || 0 }}</div><div class="home-kpi-label">在库产品数</div></el-card></el-col>
          <el-col :xs="12" :sm="6"><el-card shadow="hover" class="home-kpi-card home-kpi-warning" @click.native="openMenu('inventory/warning', '库存预警')"><div class="home-kpi-value">{{ inventoryKpi.warningCount || 0 }}</div><div class="home-kpi-label">预警条目</div></el-card></el-col>
        </el-row>

        <template v-else-if="widgetKey === 'kpiCards' && kpiCards.length">
          <div class="home-section-title">待办统计</div>
          <el-row :gutter="12">
            <el-col v-for="item in kpiCards" :key="item.key" :xs="12" :sm="8" :md="6" :lg="4">
              <el-card shadow="hover" class="home-pending-card" @click.native="openMenu(item.menuUrl, item.label)">
                <div class="home-pending-count">{{ item.count }}</div>
                <div class="home-pending-label">{{ item.label }}</div>
              </el-card>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="widgetKey === 'shortcuts'">
          <div class="home-section-title">快捷入口</div>
          <div class="home-shortcut-grid">
            <div v-for="item in displayShortcuts" :key="item.key" class="home-shortcut-item" @click="openMenu(item.menuUrl, item.title)">
              <span class="home-shortcut-icon">{{ item.icon }}</span>
              <span class="home-shortcut-title">{{ item.title }}</span>
            </div>
          </div>
          <div v-if="recentMenus.length" class="home-recent">
            <span class="home-recent-label">最近访问：</span>
            <el-tag v-for="item in recentMenus" :key="item.menuUrl" size="small" class="home-recent-tag" @click.native="openMenu(item.menuUrl, item.title)">{{ item.title }}</el-tag>
          </div>
        </template>

        <template v-else-if="widgetKey === 'todo' && todos.length">
          <div class="home-section-title">待审单据（Top {{ todos.length }}）</div>
          <el-table :data="todos" border size="small" :header-cell-style="headStyle">
            <el-table-column type="index" label="序号" width="55" align="center" />
            <el-table-column prop="docType" label="类型" width="110" />
            <el-table-column prop="docCode" label="单号" min-width="150">
              <template slot-scope="scope"><el-link type="primary" @click="openMenu(scope.row.menuUrl, scope.row.docType)">{{ scope.row.docCode }}</el-link></template>
            </el-table-column>
            <el-table-column prop="bizDate" label="业务日期" width="160" />
            <el-table-column prop="createByName" label="制单人" width="100" />
          </el-table>
        </template>

        <template v-else-if="widgetKey === 'alerts' && alerts.length">
          <div class="home-section-title">预警</div>
          <el-alert v-for="item in alerts" :key="item.key" :title="item.message + '：' + item.count" :type="item.level === 'danger' ? 'error' : 'warning'" show-icon :closable="false" class="home-alert-item" @click.native="openMenu(item.menuUrl, item.message)" />
        </template>

        <el-row v-else-if="widgetKey === 'charts' && showCharts" :gutter="12">
          <el-col v-if="inventoryTrend.length" :xs="24" :lg="12">
            <el-card shadow="never">
              <div slot="header">近30天出入库趋势</div>
              <div class="bar-chart">
                <div v-for="row in inventoryTrend" :key="row.bizDate" class="bar-col" :title="row.bizDate + ' 入' + row.inQty + ' 出' + row.outQty">
                  <div class="bar in" :style="{ height: barHeight(row.inQty, trendMax) }" />
                  <div class="bar out" :style="{ height: barHeight(row.outQty, trendMax) }" />
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col v-if="salesTrend.length" :xs="24" :lg="12">
            <el-card shadow="never">
              <div slot="header">近30天销售出库量</div>
              <div class="bar-chart">
                <div v-for="row in salesTrend" :key="'s' + row.bizDate" class="bar-col" :title="row.bizDate + ' ' + row.outQty">
                  <div class="bar sale" :style="{ height: barHeight(row.outQty, salesMax) }" />
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col v-if="inventoryStructure.length" :span="24">
            <el-card shadow="never" style="margin-top:12px">
              <div slot="header">库存结构 Top5（按分类）</div>
              <div v-for="row in inventoryStructure" :key="row.classifyName" class="struct-row">
                <span class="struct-name">{{ row.classifyName }}</span>
                <span class="struct-bar"><i :style="{ width: barWidth(row.quantity) }" /></span>
                <span>{{ formatNum(row.quantity) }}</span>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </section>
    </div>

    <div v-show="homeViewMode === 'flow'" class="home-flow-panel">
      <p class="home-recent-label">点击节点可跳转对应功能（需菜单权限）</p>
      <el-tabs v-model="flowTab" type="border-card">
        <el-tab-pane v-for="panel in flowPanels" :key="panel.key" :label="panel.title" :name="panel.key">
          <div v-for="lane in panel.lanes" :key="lane.title" class="flow-lane">
            <div class="flow-lane-head" :style="{ borderLeftColor: lane.color }">
              {{ lane.title }}
              <span v-if="lane.mergeLabel" class="flow-tag">{{ lane.mergeLabel }}</span>
            </div>
            <div class="flow-steps">
              <template v-for="(step, idx) in lane.steps">
                <div :key="step.label + idx" class="flow-step" :class="{ disabled: !canOpen(step) }" :style="{ borderColor: lane.color }" @click="openStep(step)">
                  <div>{{ step.label }}</div>
                  <div class="flow-action">{{ step.action }}</div>
                </div>
                <span v-if="idx < lane.steps.length - 1" :key="'a' + idx" class="flow-arrow">→</span>
              </template>
            </div>
          </div>
          <ul v-if="panel.notes" class="flow-notes">
            <li v-for="note in panel.notes" :key="note">{{ note }}</li>
          </ul>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog title="首页布局设置" :visible.sync="configVisible" width="520px" append-to-body>
      <p class="home-config-hint">调整区块顺序；取消勾选可隐藏区块。收藏快捷入口后优先展示（最多 6 个）。</p>
      <div v-for="(key, index) in configOrder" :key="key" class="home-config-row">
        <el-checkbox v-model="configVisibleMap[key]">{{ widgetTitle(key) }}</el-checkbox>
        <span>
          <el-button size="mini" :disabled="index === 0" @click="moveWidget(index, -1)">上移</el-button>
          <el-button size="mini" :disabled="index === configOrder.length - 1" @click="moveWidget(index, 1)">下移</el-button>
        </span>
      </div>
      <div class="home-config-fav">
        <div class="home-section-title">收藏快捷入口</div>
        <el-checkbox-group v-model="favoriteKeys">
          <el-checkbox v-for="s in allShortcuts" :key="s.key" :label="s.key">{{ s.title }}</el-checkbox>
        </el-checkbox-group>
      </div>
      <span slot="footer">
        <el-button @click="resetWidgetConfig">恢复默认</el-button>
        <el-button type="primary" @click="applyWidgetConfig">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../api/request'
import { BUSINESS_FLOW_PANELS } from '../utils/home-flow'
import {
  DEFAULT_WIDGET_ORDER, DEFAULT_SHORTCUTS, WIDGET_META,
  getWidgetConfig, saveWidgetConfig, getFavoriteShortcuts, saveFavoriteShortcuts,
  resolveVisibleWidgets, getHomeViewMode, saveHomeViewMode, menuPath, recordRecentMenu, getRecentMenus
} from '../utils/home-widgets'

export default {
  name: 'Home',
  data() {
    const cfg = getWidgetConfig()
    const visibleMap = {}
    DEFAULT_WIDGET_ORDER.forEach(k => { visibleMap[k] = (cfg.hidden || []).indexOf(k) === -1 })
    return {
      homeViewMode: getHomeViewMode(),
      flowTab: 'overview',
      flowPanels: BUSINESS_FLOW_PANELS,
      loading: false,
      todayText: '',
      financePeriodLabel: '',
      inventoryKpi: null,
      kpiCards: [],
      todos: [],
      alerts: [],
      inventoryTrend: [],
      inventoryStructure: [],
      salesTrend: [],
      serverShortcuts: [],
      recentMenus: [],
      permissions: [],
      roleFlags: {},
      widgetConfig: cfg,
      configVisible: false,
      configOrder: cfg.order.slice(),
      configVisibleMap: visibleMap,
      favoriteKeys: getFavoriteShortcuts(),
      headStyle: { background: '#f5f7fa', color: '#606266' }
    }
  },
  computed: {
    displayName() {
      const user = this.$store.state.user || {}
      return user.nickname || user.username || '用户'
    },
    tenantName() {
      const user = this.$store.state.user || {}
      return user.tenantName || ''
    },
    workbenchWidgets() {
      return resolveVisibleWidgets(this.widgetConfig, this.roleFlags)
    },
    allShortcuts() {
      const map = {}
      DEFAULT_SHORTCUTS.concat(this.serverShortcuts || []).forEach(s => {
        if (!s.perm || this.isAuth(s.perm)) map[s.key] = s
      })
      return Object.values(map)
    },
    displayShortcuts() {
      const fav = (this.favoriteKeys || []).map(k => this.allShortcuts.find(s => s.key === k)).filter(Boolean).slice(0, 6)
      return fav.length ? fav : this.allShortcuts.slice(0, 6)
    },
    showCharts() {
      return this.inventoryTrend.length || this.salesTrend.length || this.inventoryStructure.length
    },
    trendMax() {
      return Math.max(1, ...this.inventoryTrend.map(r => Math.max(Number(r.inQty || 0), Number(r.outQty || 0))))
    },
    salesMax() {
      return Math.max(1, ...this.salesTrend.map(r => Number(r.outQty || 0)))
    },
    structureMax() {
      return Math.max(1, ...this.inventoryStructure.map(r => Number(r.quantity || 0)))
    }
  },
  created() { this.loadData() },
  methods: {
    widgetTitle(key) { return (WIDGET_META[key] && WIDGET_META[key].title) || key },
    isAuth(perm) { return !perm || this.permissions.indexOf(perm) !== -1 },
    canOpen(step) { return this.isAuth(step.perm) },
    onViewModeChange(mode) { saveHomeViewMode(mode) },
    formatNum(val) {
      const n = Number(val)
      return Number.isNaN(n) ? (val || 0) : n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    barHeight(val, max) { return Math.max(4, Math.round(Number(val || 0) / max * 72)) + 'px' },
    barWidth(val) { return Math.round(Number(val || 0) / this.structureMax * 100) + '%' },
    loadData() {
      this.loading = true
      this.recentMenus = getRecentMenus()
      request.get('/home/dashboard/overview').then(res => {
        const body = res.data || {}
        const welcome = body.welcome || {}
        this.todayText = this.formatWelcomeDate(welcome.today)
        this.financePeriodLabel = welcome.financePeriod && welcome.financePeriod.label ? ('会计期间 ' + welcome.financePeriod.label) : ''
        this.inventoryKpi = body.inventoryKpi
        this.kpiCards = body.kpiCards || []
        this.todos = body.todos || []
        this.alerts = body.alerts || []
        this.inventoryTrend = body.inventoryTrend || []
        this.inventoryStructure = body.inventoryStructure || []
        this.salesTrend = body.salesTrend || []
        this.serverShortcuts = body.shortcuts || []
        this.permissions = body.permissions || []
        this.roleFlags = body.roleFlags || {}
      }).catch(() => {}).finally(() => { this.loading = false })
    },
    formatWelcomeDate(dateStr) {
      const d = dateStr ? new Date(String(dateStr).replace(/-/g, '/')) : new Date()
      if (Number.isNaN(d.getTime())) return dateStr || ''
      return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })
    },
    openMenu(menuUrl, title) {
      const path = menuPath(menuUrl)
      if (!path) return
      recordRecentMenu(menuUrl, title)
      this.recentMenus = getRecentMenus()
      this.$router.push(path).catch(() => {})
    },
    openStep(step) {
      if (!this.canOpen(step)) {
        this.$message.warning('无菜单权限：' + step.label)
        return
      }
      this.openMenu(step.menuUrl, step.label)
    },
    moveWidget(index, step) {
      const next = index + step
      const order = this.configOrder.slice()
      const item = order.splice(index, 1)[0]
      order.splice(next, 0, item)
      this.configOrder = order
    },
    applyWidgetConfig() {
      const hidden = DEFAULT_WIDGET_ORDER.filter(k => !this.configVisibleMap[k])
      this.widgetConfig = { order: this.configOrder.slice(), hidden }
      saveWidgetConfig(this.widgetConfig)
      saveFavoriteShortcuts(this.favoriteKeys)
      this.configVisible = false
      this.$message.success('布局已保存')
    },
    resetWidgetConfig() {
      this.configOrder = DEFAULT_WIDGET_ORDER.slice()
      const visibleMap = {}
      DEFAULT_WIDGET_ORDER.forEach(k => { visibleMap[k] = true })
      this.configVisibleMap = visibleMap
      this.favoriteKeys = []
    }
  }
}
</script>

<style scoped>
.mod-home { line-height: 1.5; padding: 4px 2px 24px; }
.home-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
.home-toolbar-actions { display: flex; gap: 8px; }
.home-section { margin-bottom: 16px; }
.home-section-title { font-size: 14px; font-weight: 600; color: #606266; margin-bottom: 10px; }
.home-welcome-card { background: linear-gradient(135deg, #f0fafb 0%, #ffffff 60%); border: 1px solid #e4f4f6; }
.home-welcome-title { margin: 0 0 6px; font-size: 22px; color: #303133; }
.home-welcome-sub { margin: 0; color: #909399; font-size: 13px; }
.home-welcome-period { color: #1aa5b7; }
.home-kpi-card, .home-pending-card { cursor: pointer; text-align: center; margin-bottom: 8px; }
.home-kpi-value { font-size: 22px; font-weight: 600; color: #1aa5b7; }
.home-kpi-warning .home-kpi-value { color: #e6a23c; }
.home-kpi-label, .home-pending-label { margin-top: 4px; font-size: 13px; color: #909399; }
.home-pending-count { font-size: 26px; font-weight: 600; color: #f56c6c; }
.home-shortcut-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.home-shortcut-item { width: 108px; padding: 14px 8px; text-align: center; border: 1px solid #ebeef5; border-radius: 6px; cursor: pointer; background: #fff; }
.home-shortcut-item:hover { border-color: #1aa5b7; }
.home-shortcut-icon { display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: center; border-radius: 8px; background: #e8f7f9; color: #1aa5b7; font-weight: 600; }
.home-shortcut-title { display: block; margin-top: 8px; font-size: 13px; }
.home-recent { margin-top: 12px; font-size: 13px; }
.home-recent-label { color: #909399; margin-right: 6px; }
.home-recent-tag { cursor: pointer; margin: 0 6px 6px 0; }
.home-alert-item { margin-bottom: 8px; cursor: pointer; }
.bar-chart { display: flex; align-items: flex-end; gap: 4px; height: 96px; overflow-x: auto; }
.bar-col { display: flex; align-items: flex-end; gap: 2px; min-width: 10px; }
.bar { width: 6px; border-radius: 2px 2px 0 0; }
.bar.in { background: #1aa5b7; }
.bar.out { background: #e6a23c; }
.bar.sale { background: #409EFF; width: 10px; }
.struct-row { display: flex; align-items: center; gap: 8px; margin: 8px 0; font-size: 13px; }
.struct-name { width: 120px; }
.struct-bar { flex: 1; background: #f5f7fa; height: 10px; border-radius: 5px; }
.struct-bar i { display: block; height: 10px; background: #1aa5b7; border-radius: 5px; }
.flow-lane { margin-bottom: 14px; }
.flow-lane-head { border-left: 4px solid #409EFF; padding-left: 8px; font-weight: 600; margin-bottom: 8px; }
.flow-tag { margin-left: 8px; color: #909399; font-weight: 400; font-size: 12px; }
.flow-steps { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.flow-step { border: 1px solid #dcdfe6; border-radius: 6px; padding: 8px 10px; cursor: pointer; background: #fff; min-width: 96px; }
.flow-step.disabled { opacity: 0.45; cursor: not-allowed; }
.flow-action { color: #909399; font-size: 12px; margin-top: 2px; }
.flow-arrow { color: #c0c4cc; }
.flow-notes { color: #606266; font-size: 13px; padding-left: 18px; }
.home-config-hint { color: #909399; font-size: 13px; }
.home-config-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
.home-config-fav { margin-top: 16px; }
</style>
